import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Receipt, Search, Eye, CheckCircle2, XCircle, Download, FileText, BadgeCheck, FileCheck2, CircleDollarSign, Clock3 } from 'lucide-react';
import { formatCurrency } from '../dashboards/shared';
import { buildApiUrl } from '../../utils/apiSetup';

const apiHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('access_token')}`,
});

const statusClasses = {
  Draft: 'bg-slate-100 text-slate-700',
  Submitted: 'bg-blue-100 text-blue-700',
  'Pending Review': 'bg-amber-100 text-amber-700',
  Approved: 'bg-emerald-100 text-emerald-700',
  Rejected: 'bg-rose-100 text-rose-700',
};

const rejectionOptions = ['Invalid Bill', 'Duplicate Claim', 'Missing Documents', 'Expense Not Allowed', 'Policy Violation', 'Amount Exceeded', 'Other'];

const ReimbursementReviewPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [claims, setClaims] = useState([]);
  const [summary, setSummary] = useState({
    total_claims: 0,
    pending_claims: 0,
    approved_claims: 0,
    rejected_claims: 0,
    total_claimed_amount: 0,
    total_approved_amount: 0,
  });
  const [search, setSearch] = useState('');
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [reviewingClaimId, setReviewingClaimId] = useState(null);
  const [reviewType, setReviewType] = useState('approve');
  const [approvedAmount, setApprovedAmount] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [adminRemarks, setAdminRemarks] = useState('');
  const [message, setMessage] = useState('');
  const [previewDocument, setPreviewDocument] = useState(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const [claimsRes, summaryRes] = await Promise.all([
        fetch(buildApiUrl('/api/v1/reimbursements/claims'), { headers: apiHeaders() }),
        fetch(buildApiUrl('/api/v1/reimbursements/admin/dashboard'), { headers: apiHeaders() }),
      ]);
      if (claimsRes.ok) {
        const claimsData = await claimsRes.json();
        setClaims(claimsData || []);
      }
      if (summaryRes.ok) {
        const summaryData = await summaryRes.json();
        setSummary(summaryData || {});
      }
    } catch (error) {
      setMessage('Unable to load reimbursement review queue.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredClaims = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return claims;
    return claims.filter((claim) => [claim.claim_number, claim.employee_name, claim.department, claim.category_name, claim.status].join(' ').toLowerCase().includes(q));
  }, [claims, search]);

  const openClaim = async (claim) => {
    try {
      const response = await fetch(buildApiUrl(`/api/v1/reimbursements/claims/${claim.id}`), { headers: apiHeaders() });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Unable to load claim');
      setSelectedClaim(data);
      setApprovedAmount(data.claimed_amount || '');
      setReviewingClaimId(claim.id);
      setReviewType('approve');
      setRejectionReason('');
      setAdminRemarks('');
    } catch (error) {
      setMessage(error.message || 'Unable to load claim');
    }
  };

  const handleReview = async (event) => {
    event.preventDefault();
    try {
      const payload = {
        decision: reviewType === 'approve' ? 'Approve' : 'Reject',
        approved_amount: Number(approvedAmount || 0),
        admin_remarks: adminRemarks,
        rejection_reason: reviewType === 'reject' ? rejectionReason : null,
      };
      const response = await fetch(buildApiUrl(`/api/v1/reimbursements/claims/${reviewingClaimId}/review`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...apiHeaders() },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Unable to complete review');
      setMessage(`Claim ${reviewType === 'approve' ? 'approved' : 'rejected'} successfully.`);
      setSelectedClaim(data);
      await loadData();
    } catch (error) {
      setMessage(error.message || 'Unable to complete review');
    }
  };

  const previewUrl = (documentItem) => `/api/v1/reimbursements/documents/${documentItem.id}/download`;
  const isFinalized = selectedClaim ? ['Approved', 'Rejected'].includes(selectedClaim.status) : false;

  if (loading) {
    return <div className="rounded-2xl bg-white p-8 shadow-sm">Loading reimbursement review queue…</div>;
  }

  return (
    <div className="space-y-6">
      {message ? <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">{message}</div> : null}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-blue-600 to-indigo-700 p-5 text-white shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-100">Pending</p>
              <p className="text-3xl font-semibold">{summary.pending_claims}</p>
            </div>
            <div className="rounded-full bg-white/20 p-3"><Clock3 className="h-6 w-6" /></div>
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Approved</p>
              <p className="text-3xl font-semibold text-slate-900">{summary.approved_claims}</p>
            </div>
            <div className="rounded-full bg-emerald-100 p-3"><CheckCircle2 className="h-6 w-6 text-emerald-700" /></div>
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Rejected</p>
              <p className="text-3xl font-semibold text-slate-900">{summary.rejected_claims}</p>
            </div>
            <div className="rounded-full bg-rose-100 p-3"><XCircle className="h-6 w-6 text-rose-700" /></div>
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Total Claim Amount</p>
              <p className="text-2xl font-semibold text-slate-900">{formatCurrency(summary.total_claimed_amount || 0)}</p>
            </div>
            <div className="rounded-full bg-violet-100 p-3"><Receipt className="h-6 w-6 text-violet-700" /></div>
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Total Approved Amount</p>
              <p className="text-2xl font-semibold text-slate-900">{formatCurrency(summary.total_approved_amount || 0)}</p>
            </div>
            <div className="rounded-full bg-blue-100 p-3"><CircleDollarSign className="h-6 w-6 text-blue-700" /></div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Review Claims</h3>
            <p className="text-sm text-slate-500">Approve or reject reimbursement claims with complete audit context.</p>
          </div>
          <label className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
            <Search className="h-4 w-4" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search claims" className="w-full bg-transparent outline-none" />
          </label>
        </div>

        <div className="overflow-hidden rounded-xl border border-slate-200">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-slate-600">
              <tr>
                <th className="px-4 py-3">Claim #</th>
                <th className="px-4 py-3">Employee Code</th>
                <th className="px-4 py-3">Employee Name</th>
                <th className="px-4 py-3">Department</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Claim Amount</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {filteredClaims.map((claim) => (
                <tr key={claim.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-900">{claim.claim_number}</td>
                  <td className="px-4 py-3 text-slate-600">{claim.employee_code || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{claim.employee_name || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{claim.department || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{claim.category_name || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{formatCurrency(Number(claim.claimed_amount || 0), claim.currency || 'INR')}</td>
                  <td className="px-4 py-3"><span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusClasses[claim.status] || 'bg-slate-100 text-slate-700'}`}>{claim.status}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => openClaim(claim)} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-slate-700"><Eye className="h-4 w-4" />View</button>
                      {claim.status !== 'Approved' && claim.status !== 'Rejected' ? (
                        <>
                          <button onClick={() => { setReviewingClaimId(claim.id); setReviewType('approve'); setApprovedAmount(claim.claimed_amount || ''); setSelectedClaim(claim); }} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-white">Approve</button>
                          <button onClick={() => { setReviewingClaimId(claim.id); setReviewType('reject'); setSelectedClaim(claim); }} className="rounded-lg bg-rose-600 px-3 py-1.5 text-white">Reject</button>
                        </>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selectedClaim ? (
        <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-slate-900">Claim Details</h3>
              <p className="text-sm text-slate-500">{selectedClaim.claim_number}</p>
            </div>

            <div className="space-y-4">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <h4 className="mb-3 font-semibold text-slate-900">Employee Details</h4>
                <div className="grid gap-3 md:grid-cols-2">
                  <div><p className="text-xs uppercase tracking-wide text-slate-500">Employee Code</p><p className="text-sm font-medium text-slate-900">{selectedClaim.employee_code || '—'}</p></div>
                  <div><p className="text-xs uppercase tracking-wide text-slate-500">Employee Name</p><p className="text-sm font-medium text-slate-900">{selectedClaim.employee_name || '—'}</p></div>
                  <div><p className="text-xs uppercase tracking-wide text-slate-500">Department</p><p className="text-sm font-medium text-slate-900">{selectedClaim.department || '—'}</p></div>
                  <div><p className="text-xs uppercase tracking-wide text-slate-500">Designation</p><p className="text-sm font-medium text-slate-900">{selectedClaim.designation || '—'}</p></div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <h4 className="mb-3 font-semibold text-slate-900">Expense Details</h4>
                <div className="grid gap-3 md:grid-cols-2">
                  <div><p className="text-xs uppercase tracking-wide text-slate-500">Category</p><p className="text-sm font-medium text-slate-900">{selectedClaim.category_name || '—'}</p></div>
                  <div><p className="text-xs uppercase tracking-wide text-slate-500">Expense Type</p><p className="text-sm font-medium text-slate-900">{selectedClaim.expense_type}</p></div>
                  <div><p className="text-xs uppercase tracking-wide text-slate-500">Claim Amount</p><p className="text-sm font-medium text-slate-900">{formatCurrency(Number(selectedClaim.claimed_amount || 0), selectedClaim.currency || 'INR')}</p></div>
                  <div><p className="text-xs uppercase tracking-wide text-slate-500">Status</p><p className="text-sm font-medium text-slate-900">{selectedClaim.status}</p></div>
                  <div className="md:col-span-2"><p className="text-xs uppercase tracking-wide text-slate-500">Description</p><p className="text-sm font-medium text-slate-900">{selectedClaim.description || '—'}</p></div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <h4 className="mb-3 font-semibold text-slate-900">Uploaded Documents</h4>
                <div className="flex flex-wrap gap-2">
                  {(selectedClaim.documents || []).map((document) => (
                    <div key={document.id} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                      <FileText className="h-4 w-4" />
                      <span>{document.file_name}</span>
                      <button type="button" onClick={() => setPreviewDocument(document)} className="text-blue-600">Preview</button>
                      <a href={previewUrl(document)} target="_blank" rel="noreferrer" className="text-emerald-600"><Download className="h-4 w-4" /></a>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="mb-4 text-lg font-semibold text-slate-900">Review Actions</h3>
            {isFinalized ? (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                <p className="font-semibold text-slate-900">{selectedClaim.status === 'Approved' ? 'Claim already approved.' : 'Claim already rejected.'}</p>
                <p className="mt-1">This claim is read-only and cannot be modified further.</p>
              </div>
            ) : (
              <form onSubmit={handleReview} className="space-y-4">
                <div className="flex gap-2">
                  <button type="button" onClick={() => setReviewType('approve')} className={`rounded-lg px-3 py-2 text-sm font-semibold ${reviewType === 'approve' ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-700'}`}>Approve</button>
                  <button type="button" onClick={() => setReviewType('reject')} className={`rounded-lg px-3 py-2 text-sm font-semibold ${reviewType === 'reject' ? 'bg-rose-600 text-white' : 'bg-slate-100 text-slate-700'}`}>Reject</button>
                </div>
                {reviewType === 'approve' ? (
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">Approved Amount</label>
                    <input type="number" step="0.01" value={approvedAmount} onChange={(e) => setApprovedAmount(e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2" />
                  </div>
                ) : (
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">Reason</label>
                    <select value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2">
                      <option value="">Select reason</option>
                      {rejectionOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Remarks</label>
                  <textarea value={adminRemarks} onChange={(e) => setAdminRemarks(e.target.value)} rows="4" className="w-full rounded-lg border border-slate-300 px-3 py-2" />
                </div>
                <button type="submit" className={`w-full rounded-lg px-4 py-2 text-sm font-semibold text-white ${reviewType === 'approve' ? 'bg-emerald-600' : 'bg-rose-600'}`}>{reviewType === 'approve' ? 'Approve Claim' : 'Reject Claim'}</button>
              </form>
            )}
          </div>
        </div>
      ) : null}

      {previewDocument ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4">
          <div className="w-full max-w-5xl rounded-2xl bg-white p-4 shadow-xl">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">{previewDocument.file_name}</h3>
              <button onClick={() => setPreviewDocument(null)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700">Close</button>
            </div>
            <div className="h-[70vh] overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
              {previewDocument.file_type?.toLowerCase() === 'pdf' ? (
                <iframe title={previewDocument.file_name} src={previewUrl(previewDocument)} className="h-full w-full" />
              ) : (
                <img src={previewUrl(previewDocument)} alt={previewDocument.file_name} className="h-full w-full object-contain" />
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ReimbursementReviewPage;
